import React from 'react';
import { CalendarMode, EventType, allEventTypes } from '../types';
import { getIntl } from '../../../../utils/intl';
import Stack from '../../Stack';
import Text from '../../Text';
import CalendarModeToggle from './CalendarModeToggle';
import { PRScrollView } from '../../../shared/Scroll';
import ToggleSet from '../../ToggleSet';
import { eventTypeColorMap } from '../useCalendarEvents';
import Divider from '../../Divider';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { useProfilesTabNavigation } from '../../../../utils/appNavigationNames';
import Button from '../../Button';
import { useHasPermission } from '../../Permissions/hooks';
import { categoryToDomain } from '../../../../utils/resourceUtils/medicalDataPermissions';

const intl = getIntl('activityCalendarHeader', {
    calendar: 'Activity Calendar',
    list: 'Activity List',
    all: 'All',
    visits: 'Visits',
    recordUpdates: 'Records',
    collaboration: 'Collaboration',
    profile: 'Profile',
    addVisit: 'Add Visit',
});

export type ActivityCalendarHeaderOwnProps = {
    calendarMode: CalendarMode;
    setCalendarMode: (mode: CalendarMode) => void;
    eventType: EventType;
    setEventType: (eventType: EventType) => void;
    profileId: string;
};

export type ActivityCalendarHeaderProps = ActivityCalendarHeaderOwnProps;

const ActivityCalendarHeader: React.FC<ActivityCalendarHeaderProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const { navigate } = useProfilesTabNavigation();
    const canEditVisits = useHasPermission(props.profileId, categoryToDomain('visit'), 'edit');
    return (
        <Stack vertical fullWidth>
            <Stack
                horizontal
                justify="space-between"
                align="center"
                fullWidth
                padding={4}
                style={{ paddingBottom: 4, paddingTop: 4 }}
            >
                <Stack horizontal align="center" space={4}>
                    <Text variant="bold-h5">{intl(props.calendarMode === 'calendar' ? 'calendar' : 'list')}</Text>
                    {isMobile || !canEditVisits ? null : (
                        <Button
                            variant="ghost"
                            onPress={() =>
                                navigate('add-data', {
                                    profileId: props.profileId,
                                    category: 'visit',
                                    sourceResourceId: null,
                                })
                            }
                            title={intl('addVisit')}
                            icon="Plus"
                            testID="add-visit-from-list"
                        />
                    )}
                </Stack>
                <CalendarModeToggle calendarMode={props.calendarMode} setCalendarMode={props.setCalendarMode} />
            </Stack>
            <Divider horizontal style={{ marginBottom: 4, backgroundColor: isMobile ? undefined : 'transparent' }} />
            <PRScrollView horizontal style={{ alignSelf: 'stretch' }}>
                <Stack horizontal padding={4} style={{ paddingBottom: 8 }}>
                    <ToggleSet
                        value={props.eventType}
                        onChange={props.setEventType as any}
                        options={allEventTypes.map((eventType) => ({
                            testId: `calendar-event-type-${eventType}`,
                            title: intl(eventType),
                            value: eventType,
                            color: eventTypeColorMap[eventType],
                        }))}
                    />
                </Stack>
            </PRScrollView>
        </Stack>
    );
};

export default React.memo(ActivityCalendarHeader);
